/**
 * useFloorPlanConfigDraft — the floorPlanConfig module's ops hook (the ONLY store
 * write path). MODULE-CONTRACT §2.
 *
 * The module's state IS its flow DTO at `productConfigs["${cartItemId}|floor_plan_config"]`;
 * this hook wraps it with a typed field-merged read and a single `patch` writer. No
 * imperative handle, no serialize/hydrate — `serializeDraft` / `hydrateFromDraft`
 * already round-trip `productConfigs`.
 *
 * `patch` is inherently the USER-EDIT boundary — hydration never calls it (§3), and
 * the logo sync only calls it AFTER the first user interaction this session
 * (interaction-gated, §4b) — so it un-confirms unconditionally via `markConfigDirty`.
 *
 * ⚠️ Nested-object caveat (contract §2): the field-merge is SHALLOW — it does NOT
 * deep-default `dimensions`, `floorPlanProductConfig`, or the `outputFileFormat`/
 * `fileOrientation` arrays. The UI/preview index them defensively
 * (`dto.dimensions?.showDimensionChain ?? false`, `dto.floorPlanProductConfig ?? {}`,
 * `dto.outputFileFormat ?? []`), exactly like legacy did.
 */

import { useCallback, useEffect, useMemo } from 'react'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { makeConfigKey } from '../../store/slices/productConfigSlice'
import { useFloorPlanLineIds } from './useFloorPlanLineIds'
import { FLOOR_PLAN_CONFIG_DEFAULT, floorPlanConfigIsComplete, type FloorPlanConfigDraftDTO } from './floorPlanConfig.dto'

export interface UseFloorPlanConfigDraft {
  /** Field-complete DTO — safe to read every TOP-LEVEL field, even against a partial
   *  or absent persisted entry (§2 partial-DTO invariant). Nested objects/arrays
   *  still need defensive indexing (shallow merge). */
  value: FloorPlanConfigDraftDTO
  /** Merge-and-write a partial DTO. Idempotency-guarded by `setConfig`; flips the
   *  tab back to unconfirmed via `markConfigDirty`. */
  patch: (partial: Partial<FloorPlanConfigDraftDTO>) => void
  isComplete: boolean
}

export function useFloorPlanConfigDraft(cartItemId: CartItemId): UseFloorPlanConfigDraft {
  const key = makeConfigKey(cartItemId, 'floor_plan_config')
  const lineIds = useFloorPlanLineIds()

  // Fine-grained subscription: re-renders only when THIS item's DTO changes.
  const raw = usePfStore((s) => s.productConfigs[key] as Partial<FloorPlanConfigDraftDTO> | undefined)
  // Every floor-plan line shares one configuration, so a line opened for the first time shows the
  // answer already given on another one.
  const sharedWithLineSettings = usePfStore((s) => sharedFloorPlanConfig(s.productConfigs, lineIds, cartItemId))
  const shared = useMemo(() => withoutLineSettings(sharedWithLineSettings), [sharedWithLineSettings])

  // FIELD-LEVEL merge over the default — NOT `raw ?? DEFAULT`. A persisted DTO is
  // legitimately partial, so this read boundary IS the top-level normalizer.
  const value: FloorPlanConfigDraftDTO = useMemo(() => ({ ...FLOOR_PLAN_CONFIG_DEFAULT, ...(raw ?? shared) }), [raw, shared])

  // Stores the shared answer on a line that has none yet, so its own completeness and placement
  // read it like any other line's.
  useEffect(() => {
    if (raw || !shared) return
    usePfStore.getState().patchConfig(cartItemId, 'floor_plan_config', shared)
  }, [raw, shared, cartItemId])

  const patch = useCallback((partial: Partial<FloorPlanConfigDraftDTO>) => {
    const s = usePfStore.getState()
    const current: FloorPlanConfigDraftDTO = {
      ...FLOOR_PLAN_CONFIG_DEFAULT,
      ...((s.productConfigs[key] ?? withoutLineSettings(sharedFloorPlanConfig(s.productConfigs, lineIds, cartItemId))) as Partial<FloorPlanConfigDraftDTO> | undefined),
    }
    s.patchConfig(cartItemId, 'floor_plan_config', { ...current, ...partial })
    const sharedPartial = withoutLineSettings(partial) ?? {}
    // A change to this line's own settings only (its editing scope) touches no other line.
    if (Object.keys(sharedPartial).length === 0) return
    // The change reaches every other floor-plan line, merged into what that line holds, so a line
    // kept at 2D because it cannot bill 3D does not take the render type back to the others. Each
    // line it really changed is un-confirmed and has to be saved again.
    for (const lineId of lineIds) {
      if (lineId === cartItemId) continue
      const own = s.productConfigs[makeConfigKey(lineId, 'floor_plan_config')] as Partial<FloorPlanConfigDraftDTO> | undefined
      // What to draw from a client's plan belongs to that line's own plan, so it is never copied.
      s.patchConfig(lineId, 'floor_plan_config', { ...withoutLineSettings(current), ...own, ...sharedPartial })
    }
  }, [key, cartItemId, lineIds])

  return { value, patch, isComplete: floorPlanConfigIsComplete((raw ?? shared) as FloorPlanConfigDraftDTO | undefined) }
}

/** A configuration without the settings that belong to one line only (the editing scope of its own plan). */
const withoutLineSettings = <T extends Partial<FloorPlanConfigDraftDTO>>(config: T | undefined): Omit<T, 'floorPlanProductConfig'> | undefined => {
  if (!config) return undefined
  const { floorPlanProductConfig: _lineOnly, ...rest } = config
  return rest
}

/** The configuration another floor-plan line already holds, or undefined when none does. */
const sharedFloorPlanConfig = (
  configs: Record<string, unknown>,
  lineIds: readonly CartItemId[],
  except: CartItemId
): Partial<FloorPlanConfigDraftDTO> | undefined => {
  for (const lineId of lineIds) {
    if (lineId === except) continue
    const config = configs[makeConfigKey(lineId, 'floor_plan_config')]
    if (config) return config as Partial<FloorPlanConfigDraftDTO>
  }
  return undefined
}
